import axios from 'axios';
import {
	PageLayout,
	Card,
	CardBody,
	Button,
	CardPlaceholder,
	Permissions,
	getRegistrationUrl,
	getAdminUrl,
	getTrainingUrl,
	Icon,
	Icons,
	useAuthorisationContext,
	useLangContext,
	StringHelpers,
	Variants,
	checkTheme,
	Themes,
	route,
	API_ENDPOINTS,
	customToast,
	replaceKeyWithValue,
	Roles,
	getThemeFromUrl,
	getAppEnv,
} from 'carrier-fe';
import { useState, useEffect } from 'react';

function Support() {
	const { getLangPage } = useLangContext();
	const { hasPermissionAccess, hasRoleAccess } = useAuthorisationContext();
	const [lang, setLang] = useState<any>(null);

	useEffect(() => {
		if (lang === null) {
			load();
		}
	}, []);

	const load = async () => {
		getLangPage('dashboard/support')
			.then((res: any) => {
				if (typeof res === 'undefined' || res === null) {
					setLang([]);
				} else {
					setLang(res);

					document.title = StringHelpers.title(
						res?.page.title ?? 'Support'
					);
				}
			})
			.catch((err: any) => console.error(err));
	};

	const getTileById = (id: string) => {
		if (!lang || !lang.page || !lang.page.tiles) return null;

		for (let i = 0; i < lang.page.tiles.length; i++) {
			if (lang.page.tiles[i]['id'] === id) {
				return lang.page.tiles[i];
			}
		}

		return null;
	};

	// Access control (admins can always access):
	//  - Toshiba:   company managers.
	//  - Viessmann: company managers and engineers.
	const canAccessBrochureOrder = () => {
		if (!hasPermissionAccess(Permissions.SUPPORT_PLATFORM_VIEW)) {
			return false;
		}

		const isAdmin =
			hasRoleAccess(Roles.SUPER_ADMIN)
			|| hasRoleAccess(Roles.PLATFORM_MANAGER);

		if (checkTheme(Themes.Toshiba)) {
			return isAdmin || hasRoleAccess(Roles.COMPANY_MANAGER);
		}

		if (checkTheme(Themes.Viessmann)) {
			return (
				isAdmin
				|| hasRoleAccess(Roles.COMPANY_MANAGER)
				|| hasRoleAccess(Roles.ENGINEER)
			);
		}

		return true;
	};

	const renderBrochureOrderTile = () => {
		if (!canAccessBrochureOrder())
			return null;

		let content = getTileById('brochureOrder.dashboard');

		return (
			<Card noMargin>
				<CardBody>
					<Icon
						icon={Icons.LIST}
						style={{ color: '#ff3e17' }}
					/>
					<h4 className="card-title">
						{!!content && !!content.title
							? content.title
							: 'Order Brochures'
						}
					</h4>
					<p className="card-text mb-4">
						{!!content && !!content.description 
							? content.description
							: 'Access the brochure ordering dashboard'
						}
					</p>
					<Button
						label={
							!!content && !!content.button 
								? content.button
								: 'Go to'
						}
						href={'/support/brochure-order'}
						className="d-inline-flex"
						variant={Variants.Info}
						disabled={
							checkTheme(Themes.Viessmann) || checkTheme(Themes.Toshiba)
							? false
							: true
						}
					/>
				</CardBody>
			</Card>
		);
	};

	const renderMarketingImagesTile = () => {
		if (
			!hasPermissionAccess(Permissions.SUPPORT_PLATFORM_VIEW)
			|| (checkTheme(Themes.Toshiba) 
				&& !hasRoleAccess(Roles.PLATFORM_MANAGER) 
				&& !hasRoleAccess(Roles.SUPER_ADMIN)
				&& !hasRoleAccess(Roles.COMPANY_MANAGER)
			)
		)
			return null;

		let content = getTileById('marketingImages.dashboard');

		return (
			<Card noMargin>
				<CardBody>
					<Icon
						icon={Icons.IMAGE}
						style={{ color: '#ff3e17' }}
					/>
					<h4 className="card-title">
						{!!content && !!content.title
							? content.title
							: 'Marketing Images'
						}
					</h4>
					<p className="card-text mb-4">
						{!!content && !!content.description
							? content.description
							: 'View and download popular product images.'
						}
					</p>
					<Button
						label={
							!!content && !!content.button 
								? content.button
								: 'Go to'
						}
						href={'/support/marketing-images'}
						className="d-inline-flex"
						variant={Variants.Info}
						disabled={
							checkTheme(Themes.Viessmann) || checkTheme(Themes.Toshiba)
							? false
							: true
						}
					/>
				</CardBody>
			</Card>
		);
	};

	const renderCalloutTile = () => {
		if (
			!hasPermissionAccess(Permissions.SUPPORT_PLATFORM_VIEW)
			|| !checkTheme(Themes.Viessmann)
		)
			return null;

		let content = getTileById('callout.dashboard');

		return (
			<Card noMargin>
				<CardBody>
					<Icon
						icon={Icons.HELP}
						style={{ color: '#ff3e17' }}
					/>
					<h4 className="card-title">
						{!!content && !!content.title
							? content.title
							: 'Book Call-out'
						}
					</h4>
					<p className="card-text mb-4">
						{!!content && !!content.description
							? content.description
							: 'Book a support call-out'
						}
					</p>
					<Button
						label={
							!!content && !!content.button 
								? content.button
								: 'Go to'
						}
						href={content.href[getThemeFromUrl()]}
						className="d-inline-flex"
						variant={Variants.Info}
						externalLink
						disabled={
							checkTheme(Themes.Viessmann)
							? false
							: true
						}
					/>
				</CardBody>
			</Card>
		);
	};


	return (
		<PageLayout
			title={StringHelpers.title(lang?.page.title ?? 'Support Dashboard')}
		>
			<div className="dashboard--inner--wrapper d-grid gap-4">
				{lang === null ? (
					<>
						<CardPlaceholder />
						<CardPlaceholder />
						<CardPlaceholder />
					</>
				) : (
					<>
						{renderBrochureOrderTile()}
						{renderMarketingImagesTile()}
						{renderCalloutTile()}
					</>
				)}
			</div>
		</PageLayout>
	);
}

export default Support;
